// 261012 E1-P3 (D9–D11) — trạng thái + hành động của cột "Nhận dạng hình ảnh" ở chặng ②.
//  - Tải `GET visual-identity/:storyId` khi mount và mỗi khi `dataVersion` đổi (mọi ghi đều `await refreshAll()`); lỗi ⇒ `loadFailed`, KHÔNG làm hỏng hai nhóm cột cũ.
//  - Chạy AI: `POST` → `jobId` → `useFilmJobPoll`; sau tải lại trang NHẬN NUÔI job `FILM_VISUAL_IDENTITY` mà BE báo đang chạy. Xong ⇒ toast + `refreshAll()`.
//  - MỌI `POST` đi qua `runConfirmCopy` (hộp ước giá; 261015: không còn hộp "truyện thật").
//  - Ghi (sửa / Giữ / xoá) dùng cờ `working` DÙNG CHUNG của EntityTable/SkeletonStage, rồi `await refreshAll()`.
// Logic thuần nằm ở `visualIdentityView.ts` (test được dưới jest); file này chỉ nối API + antd.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type {
  VisualEntityKind, VisualEditBody, VisualIdentitySummary, VisualJobResult, VisualProfileView, VisualRunBody,
} from '../../../types/adaptation';
import type { JobStepProgress } from '../../../types/adaptationWorkbench';
import { useFilmJobPoll } from '../useFilmJobPoll';
import { jobFailedToast } from './jobErrorText';
import { formatJobStepProgress } from './jobStepProgress';
import { useAdoptedJobId } from './useAdoptedJobId';
import {
  buildRunBody, jobDoneText, pickVisualJobId, runConfirmCopy, RunScope, visualErrorOf,
} from './visualIdentityView';
import { useWorkbenchCtx } from './WorkbenchContext';
import type { DangerCopyView } from './workbenchView';

interface JobDetail { progress?: JobStepProgress | number; result?: VisualJobResult; error?: string; note?: string }

export interface VisualEntityRef { kind: VisualEntityKind; id: string; /** Tên hiển thị trong hộp hỏi. */ label: string }

export interface VisualIdentityApi {
  summary: VisualIdentitySummary | null;
  loadFailed: boolean;
  reload: () => void;
  /** Job nhận dạng đang chạy (của phiên này hoặc BE báo) HOẶC đang gọi POST. */
  jobRunning: boolean;
  progressText: string | null;
  maxChars: number | null;
  /** N mục còn thiếu cả truyện (`counts.eligible`). */
  eligible: number;
  viewOf: (kind: VisualEntityKind, id: string) => VisualProfileView | null;
  /** Hỏi xác nhận (ước giá) rồi mới POST. `entity` bắt buộc khi `scope:'one'`. */
  requestRun: (scope: RunScope, entity?: VisualEntityRef & { hasProfile: boolean }) => void;
  /** `true` ⇒ ô sửa nên đóng (xong, hoặc trạng thái đã cũ); `false` ⇒ lỗi nhập, giữ ô mở. */
  saveText: (e: VisualEntityRef, text: string, baseVersion: string) => Promise<boolean>;
  setLock: (e: VisualEntityRef, locked: boolean, baseVersion: string) => Promise<void>;
  /** Có hộp hỏi lại trước khi xoá. */
  requestClear: (e: VisualEntityRef, baseVersion: string) => void;
}

export interface VisualIdentityDeps {
  /** Cờ ghi DÙNG CHUNG của EntityTable/SkeletonStage (khoá nút do `cellState` quyết; hook chỉ bật/tắt khi ghi). */
  setWorking: (v: boolean) => void;
  confirmCopy: (c: DangerCopyView, run: () => void | Promise<void>) => void;
}

export const useVisualIdentity = ({ setWorking, confirmCopy }: VisualIdentityDeps): VisualIdentityApi => {
  const { storyId, dataVersion, refreshAll } = useWorkbenchCtx();
  const [summary, setSummary] = useState<VisualIdentitySummary | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [starting, setStarting] = useState(false);
  const [progressText, setProgressText] = useState<string | null>(null);
  const currentStoryIdRef = useRef(storyId);
  const inflight = useRef(false);
  currentStoryIdRef.current = storyId;

  useEffect(() => { setSummary(null); setLoadFailed(false); setProgressText(null); }, [storyId]);
  useEffect(() => {
    let alive = true;
    const forStory = storyId;
    adaptationApi.getVisualIdentity(forStory)
      .then((s) => { if (alive && currentStoryIdRef.current === forStory) { setSummary(s); setLoadFailed(false); } })
      .catch(() => { if (alive && currentStoryIdRef.current === forStory) setLoadFailed(true); });
    return () => { alive = false; };
  }, [storyId, dataVersion, retry]);

  const reload = useCallback(() => setRetry((n) => n + 1), []);

  // ── job nền ──
  const { jobId, setJobId, release } = useAdoptedJobId(storyId, pickVisualJobId(summary?.runningJobs));
  useFilmJobPoll<JobDetail>(jobId, {
    onTick: (task) => {
      const p = task.detail?.progress;
      setProgressText(p && typeof p === 'object' ? formatJobStepProgress(p) || null : null);
    },
    onTerminal: async (outcome, task) => {
      if (outcome === 'completed') message.success(jobDoneText(task?.detail?.result));
      else if (outcome === 'failed') message.error(jobFailedToast(task?.detail?.error, task?.detail?.note));
      else message.warning('Đã dừng theo dõi — việc đề xuất nhận dạng có thể vẫn chạy, bấm Tải lại.');
      release();
      setProgressText(null);
      if (outcome === 'completed' || outcome === 'failed') await refreshAll(); else reload();
    },
  });
  const jobRunning = !!jobId || starting;

  const maxChars = summary?.limits?.maxChars ?? null;
  const eligible = summary?.counts?.eligible ?? 0;
  const byId = useMemo(() => {
    const m = new Map<string, VisualProfileView | null>();
    for (const c of summary?.characters ?? []) m.set(`character:${c.id}`, c.profileView ?? null);
    for (const l of summary?.locations ?? []) m.set(`location:${l.id}`, l.profileView ?? null);
    return m;
  }, [summary]);
  const viewOf = useCallback((kind: VisualEntityKind, id: string) => byId.get(`${kind}:${id}`) ?? null, [byId]);

  // ── chạy AI (tốn tiền) ──
  const startRun = useCallback(async (body: VisualRunBody) => {
    if (inflight.current) return;
    inflight.current = true;
    setWorking(true);
    setStarting(true);
    try {
      const r = await adaptationApi.runVisualIdentity(body);
      if (!r?.jobId) {
        message.warning('Server không trả job id; đã dừng theo dõi và không tự gửi lại.');
        await refreshAll();
        return;
      }
      setJobId(r.jobId);
      message.info('Đề xuất nhận dạng đã bắt đầu ở nền', 5);
    } catch (e) {
      const d = visualErrorOf(e, maxChars);
      message.error(d.text, 6);
      if (d.reload) await refreshAll();
    } finally {
      setStarting(false);
      inflight.current = false;
      setWorking(false);
    }
  }, [setWorking, setJobId, refreshAll, maxChars]);

  const requestRun = useCallback((scope: RunScope, entity?: VisualEntityRef & { hasProfile: boolean }) => {
    if (inflight.current || jobRunning) return;
    if (scope === 'one' && !entity) return;
    const count = scope === 'one' ? 1 : eligible;
    if (count <= 0) return;
    const redo = scope === 'one' ? !!entity?.hasProfile : false;
    const { steps } = runConfirmCopy({ scope, count, redo, name: entity?.label, kind: entity?.kind });
    const body = buildRunBody({ storyId, scope, id: entity?.id, redo });
    const chain = (i: number): void | Promise<void> => (i >= steps.length ? startRun(body) : confirmCopy(steps[i], () => chain(i + 1)));
    chain(0);
  }, [jobRunning, eligible, storyId, startRun, confirmCopy]);

  // ── ghi (sửa / Giữ / xoá) ──
  const patch = useCallback(async (body: VisualEditBody, okMsg?: string): Promise<boolean> => {
    if (inflight.current) return false;
    inflight.current = true;
    setWorking(true);
    try {
      await adaptationApi.patchVisualProfile(body);
      await refreshAll();
      if (okMsg) message.success(okMsg);
      return true;
    } catch (e) {
      const d = visualErrorOf(e, maxChars);
      message.error(d.text, 6);
      if (d.reload) { await refreshAll(); return true; }
      return false;
    } finally {
      inflight.current = false;
      setWorking(false);
    }
  }, [setWorking, refreshAll, maxChars]);

  const saveText = useCallback((e: VisualEntityRef, text: string, baseVersion: string) =>
    patch({ storyId, kind: e.kind, id: e.id, op: 'edit', text, baseVersion }, 'Đã lưu — ghi là “Đã sửa tay” và Giữ'),
  [patch, storyId]);
  const setLock = useCallback(async (e: VisualEntityRef, locked: boolean, baseVersion: string) => {
    await patch({ storyId, kind: e.kind, id: e.id, op: 'setLock', locked, baseVersion });
  }, [patch, storyId]);
  const requestClear = useCallback((e: VisualEntityRef, baseVersion: string) => {
    if (inflight.current) return;
    confirmCopy({
      title: `Xoá mô tả của ${e.label}?`, danger: true, okText: 'Xoá',
      content: 'Film sẽ chỉ thấy tên, tự đoán tuổi/dáng cho đến khi bạn bấm Đề xuất hoặc Tự viết lại.',
    }, async () => { await patch({ storyId, kind: e.kind, id: e.id, op: 'clear', baseVersion }, 'Đã xoá mô tả'); });
  }, [confirmCopy, patch, storyId]);

  return { summary, loadFailed, reload, jobRunning, progressText, maxChars, eligible, viewOf, requestRun, saveText, setLock, requestClear };
};
